/* eslint-disable @typescript-eslint/camelcase */
import { Grid, makeStyles, Typography } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import clsx from 'clsx';
import Button from 'components/Button';
import Loading from 'components/Loading';
import i18n from 'i18n';
import jsPDF from 'jspdf';
import { CompanyData, RolesEnum } from 'modules/authenticate/interfaces';
import { editClientRequest } from 'modules/clients/actions';
import { ClientFields } from 'modules/clients/interfaces';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import { inAdminView, inOperatorView, inSuperAdminView } from 'utils/role';
import ClientSummary from '../ClientSummary/clientSummary';
import Form from '../Form';
import {
  getClientEnvironmentNamePDF,
  getClientLicensesPDF,
  getClientNamePDF,
} from '../helpers';
import ClientsTemplate from '../Templates/Clients';
import styles from './styles';

const useStyles = makeStyles(styles);

interface Props {
  isLoading: boolean;
  client: ClientFields | null;
  companySelected: CompanyData | null;
  role?: RolesEnum;
  editClientRequest: ({ clientUuid, values }) => void;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
}

const Configurations = ({
  isLoading,
  client,
  companySelected,
  role,
  editClientRequest,
  modalOpen,
  modalClose,
}: Props) => {
  const classes = useStyles();

  // eslint-disable-next-line new-cap
  const doc = new jsPDF({ format: 'dl', orientation: 'landscape' });

  const generatePDFButton = (client: ClientFields | null) => {
    doc.setFontSize(19);
    doc.text(
      [
        '',
        `${i18n.t('CLIENT_DATA_KEY')}:`,
        '',
        getClientNamePDF(client),
        getClientEnvironmentNamePDF(client),
        getClientLicensesPDF(client),
      ],
      10,
      10,
      { lineHeightFactor: 1.5, maxWidth: 200 }
    );
    doc.save(`${client?.name}.pdf`);
  };

  const handleOpenClientSummary = () => {
    modalOpen({
      title: i18n.t('CLIENT_DATA_KEY'),
      content: <ClientSummary client={client} />,
      footer: (
        <Grid>
          <Button
            id="generate-pdf-button"
            submitButton
            startIcon={<GetAppIcon />}
            onClick={() => {
              generatePDFButton(client);
              modalClose();
            }}
          >
            {i18n.t('GENERATE_PDF')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CLOSE_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  return (
    <ClientsTemplate
      current="configurations"
      title={i18n.t('CLIENTS_PAGE.EDIT_CLIENT_KEY')}
      breadcrumbs={[
        {
          name: i18n.t('CLIENT_KEY'),
          link: '/clients',
        },
        {
          name: i18n.t('SETTINGS_KEY'),
        },
      ]}
      button={
        <Button
          variant="outlined"
          id="client-summary-button"
          onClick={handleOpenClientSummary}
          className={clsx(classes.summaryButton, {
            [classes.superAdminButton]: inSuperAdminView(role, companySelected),
            [classes.adminButton]: inAdminView(role, companySelected),
            [classes.operatorButton]: inOperatorView(role, companySelected),
          })}
        >
          <Typography className={classes.summaryButton}>
            {i18n.t('CLIENT_DATA_SUMMARY_KEY')}
          </Typography>
        </Button>
      }
    >
      {!isLoading ? (
        <Form
          isEdit
          initialValues={client}
          onSubmit={editClientRequest}
        />
      ) : (
        <Loading />
      )}
    </ClientsTemplate>
  );
};

const mapStateToProps = ({
  clients: { clientSelected, isLoading },
  authenticate: { companySelected, data },
}: ApplicationState) => ({
  isLoading,
  client: clientSelected,
  companySelected,
  role: data?.role,
});

export default connect(mapStateToProps, {
  editClientRequest,
  modalOpen,
  modalClose,
})(Configurations);
